import { useState } from "react";
import { toast } from "sonner";
import { GraduationCap, Clock, MapPin, Award, ArrowRight } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Container, Eyebrow, Section, Reveal } from "./common";
import { CURSOS } from "../data/content";
import { api, formatApiErrorDetail } from "../lib/api";

const EnrollDialog = ({ curso }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ nombre: "", correo: "", telefono: "", organizacion: "" });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/inscripciones", { ...form, curso_id: curso.id, curso_titulo: curso.title });
      toast.success("Inscripción registrada", {
        description: "Te confirmaremos los detalles por correo. ¡Gracias por sumarte!",
      });
      setForm({ nombre: "", correo: "", telefono: "", organizacion: "" });
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo registrar la inscripción");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid={`curso-inscribir-btn-${curso.id}`}
          className="mt-6 group inline-flex items-center gap-2 text-sm text-rc-sage hover:text-rc-forestlight transition-colors"
        >
          Inscribirme a este programa
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </button>
      </DialogTrigger>
      <DialogContent className="bg-rc-card border-rc-border text-stone-100 sm:max-w-md" data-testid="enroll-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl font-light">{curso.title}</DialogTitle>
          <DialogDescription className="text-stone-400">
            {curso.duracion} · {curso.modalidad}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2" data-testid="enroll-form">
          <div>
            <Label className="text-stone-300">Nombre completo *</Label>
            <Input required data-testid="enroll-nombre" value={form.nombre} onChange={set("nombre")}
              className="mt-1.5 bg-rc-bg2 border-rc-border text-stone-100" />
          </div>
          <div>
            <Label className="text-stone-300">Correo electrónico *</Label>
            <Input required type="email" data-testid="enroll-correo" value={form.correo} onChange={set("correo")}
              className="mt-1.5 bg-rc-bg2 border-rc-border text-stone-100" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-stone-300">Teléfono</Label>
              <Input data-testid="enroll-telefono" value={form.telefono} onChange={set("telefono")}
                className="mt-1.5 bg-rc-bg2 border-rc-border text-stone-100" />
            </div>
            <div>
              <Label className="text-stone-300">Organización</Label>
              <Input data-testid="enroll-organizacion" value={form.organizacion} onChange={set("organizacion")}
                className="mt-1.5 bg-rc-bg2 border-rc-border text-stone-100" />
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="enroll-submit-btn"
            className="w-full py-3 rounded-full bg-rc-forest hover:bg-rc-forestlight text-stone-100 transition-colors disabled:opacity-60"
          >
            {loading ? "Enviando…" : "Confirmar inscripción"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const CursosPortal = () => {
  return (
    <Section id="cursos">
      <Container>
        <Reveal>
          <Eyebrow>Portal de inscripción</Eyebrow>
          <h2 className="mt-5 font-serif font-light text-3xl sm:text-4xl lg:text-5xl tracking-tight text-stone-100">
            Inscríbete a un <span className="text-rc-sage italic">programa</span>
          </h2>
          <p className="mt-6 max-w-2xl text-stone-400 font-light leading-relaxed">
            Formación con aplicación inmediata para municipios, retail y comerciantes. Elige tu programa y
            deja tus datos; el equipo te confirma los detalles por correo.
          </p>
        </Reveal>

        <div className="mt-14 grid md:grid-cols-2 gap-6 lg:gap-8">
          {CURSOS.map((c, i) => (
            <Reveal key={c.id} delay={(i % 2) * 0.1}>
              <div className="h-full flex flex-col p-8 md:p-10 rounded-2xl bg-rc-card border border-rc-border hover:border-rc-sage/40 transition-all duration-300">
                <GraduationCap className="w-6 h-6 text-rc-sage" strokeWidth={1.5} />
                <h3 className="mt-5 font-serif text-2xl text-stone-100">{c.title}</h3>
                <p className="mt-3 text-stone-400 font-light leading-relaxed flex-1">{c.text}</p>
                <div className="mt-6 flex flex-wrap gap-4 text-sm text-stone-300">
                  <span className="inline-flex items-center gap-2"><Clock className="w-4 h-4 text-rc-sage/70" />{c.duracion}</span>
                  <span className="inline-flex items-center gap-2"><MapPin className="w-4 h-4 text-rc-sage/70" />{c.modalidad}</span>
                </div>
                <div className="mt-3 inline-flex items-center gap-2 text-sm text-rc-sand">
                  <Award className="w-4 h-4" />{c.aval}
                </div>
                <EnrollDialog curso={c} />
              </div>
            </Reveal>
          ))}
        </div>
      </Container>
    </Section>
  );
};
